import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNotification } from "../../hooks/useNotification";
import {
  Plus,
  Calendar,
  Trash2,
  Save,
  X,
  Lock,
  Unlock,
  Receipt,
  MessageSquare,
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  FileText,
  Upload,
} from "lucide-react";
import {
  caixaService,
  solicitacaoAberturaCaixaService,
  formaPagamentoService,
  tipoCaixaService,
  solicitacaoReaberturaCaixaService,
  authService,
} from "../../services/apiService";
import { formatarData } from "../../utils/FormatarData";
import {
  formatarValorReal,
  formatCurrencyMoeda,
} from "../../utils/FormatarValores";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  IconButton,
  InfoTooltip,
  TextAreaInput,
  TextInput,
  TipoCaixaSelect,
  FileInput,
  buttonPageActiveClass,
  buttonPageClass,
  cardClass,
  cardHeaderClass,
  cardHeaderTitleClass,
  checkboxClass,
  tableBodyClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellCenterClass,
  tableHeaderCellClass,
  tableHeaderCellRightClass,
  tableRowClass,
  tableWrapperClass,
} from "../../components/ui";

const FUNDO_CAIXA_PADRAO = 100;
const MARCADOR_QUEBRA_FUNDO = "Quebra Fundo:";
const MARCADOR_QUEBRA_CAIXA = "Quebra Caixa:";

// Monta a observação final concatenando o motivo da quebra do fundo e/ou da
// quebra de caixa (quando houver) com a observação livre, sempre nessa ordem
// e no mesmo formato fixo, separados por uma linha em branco entre blocos.
const montarObservacaoFinal = (
  motivoQuebraFundo,
  motivoQuebraCaixa,
  observacaoLivre
) => {
  const motivoFundo = (motivoQuebraFundo || "").trim();
  const motivoCaixa = (motivoQuebraCaixa || "").trim();
  const livre = (observacaoLivre || "").trim();

  const blocos = [];
  if (motivoFundo) {
    blocos.push(`${MARCADOR_QUEBRA_FUNDO} ${motivoFundo}`);
  }
  if (motivoCaixa) {
    blocos.push(`${MARCADOR_QUEBRA_CAIXA} ${motivoCaixa}`);
  }
  if (livre) {
    blocos.push(livre);
  }

  return blocos.join("\n\n");
};

// Extrai um bloco com marcador fixo do início do texto, retornando o valor
// do bloco (sem o marcador) e o restante do texto que sobrou depois dele.
const extrairBlocoMarcado = (texto, marcador) => {
  const prefixo = `${marcador} `;
  if (!texto.startsWith(prefixo)) {
    return { valor: "", resto: texto };
  }

  const semPrefixo = texto.slice(prefixo.length);
  const indiceSeparador = semPrefixo.indexOf("\n\n");
  if (indiceSeparador === -1) {
    return { valor: semPrefixo, resto: "" };
  }

  return {
    valor: semPrefixo.slice(0, indiceSeparador),
    resto: semPrefixo.slice(indiceSeparador + 2),
  };
};

// Faz o caminho inverso de montarObservacaoFinal, usado ao reabrir o formulário
// para popular de volta os campos separados a partir do texto salvo.
const parseObservacaoSalva = (observacaoCompleta) => {
  if (!observacaoCompleta || !observacaoCompleta.trim()) {
    return { motivoQuebraFundo: "", motivoQuebraCaixa: "", observacao: "" };
  }

  const { valor: motivoQuebraFundo, resto: apósQuebraFundo } =
    extrairBlocoMarcado(observacaoCompleta, MARCADOR_QUEBRA_FUNDO);
  const { valor: motivoQuebraCaixa, resto: observacao } = extrairBlocoMarcado(
    apósQuebraFundo,
    MARCADOR_QUEBRA_CAIXA
  );

  return { motivoQuebraFundo, motivoQuebraCaixa, observacao };
};

const CaixaDiario = ({ usuario }) => {
  const [tiposCaixa, setTiposCaixa] = useState([]);
  const [caixas, setCaixas] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMovimentoForm, setShowMovimentoForm] = useState(false);
  const [showReaberturaForm, setShowReaberturaForm] = useState(false);
  const [showSolicitacaoAberturaForm, setShowSolicitacaoAberturaForm] =
    useState(false);
  const [showDataCaixaForm, setShowDataCaixaForm] = useState(false);
  const [showImportacaoForm, setShowImportacaoForm] = useState(false);
  const [importacaoTipoCaixaId, setImportacaoTipoCaixaId] = useState("");
  const [importacaoArquivo, setImportacaoArquivo] = useState(null);
  const [showObservacao, setShowObservacao] = useState(false);
  const [observacaoView, setObservacaoView] = useState("");
  const [selectedCaixa, setSelectedCaixa] = useState(null);
  const [datasImpedimento, setDatasImpedimento] = useState([]);
  const [dataCaixaSelecionada, setDataCaixaSelecionada] = useState(
    new Date().toLocaleDateString("en-CA")
  );
  const inputRefs = useRef({});

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [displayValues, setDisplayValues] = useState({
    valorRetirada: "",
    valorSangria: "",
    totalDinheiroContado: formatarValorReal(
      (FUNDO_CAIXA_PADRAO * 100).toString()
    ).formatted,
    tipoCaixa: {},
    formasPagamento: {},
  });
  // Enquanto o usuário não mexer no "Total em Dinheiro Contado" na mão, esse
  // campo se atualiza sozinho (fundo + dinheiro digitado em Formas de
  // Pagamento) pra já vir assumindo que não houve quebra nenhuma.
  const [totalDinheiroContadoManual, setTotalDinheiroContadoManual] =
    useState(false);
  const [movimentoData, setMovimentoData] = useState({
    caixaMovimento: [
      { formaPagamento: { id: "", nome: "" }, valorMovimento: 0 },
    ],
    caixaMovimentoTipoCaixa: [],
    valorRetirada: 0,
    valorSangria: 0,
    valorQuebra: 0,
    motivoQuebraCaixa: "",
    // Total físico contado na gaveta (fundo + dinheiro recebido no dia) —
    // não dá pra contar o fundo isolado, dinheiro não vem rotulado por
    // origem. Começa assumindo que os R$ 100 continuam intactos.
    totalDinheiroContado: FUNDO_CAIXA_PADRAO,
    valorQuebraFundo: 0,
    motivoQuebraFundo: "",
    observacao: "",
  });
  const [reaberturaData, setReaberturaData] = useState({
    motivo: "",
  });
  const [solicitacaoAberturaCaixa, setSolicitacaoAberturaCaixa] = useState({
    motivo: "",
  });
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();
  const dataHojeInput = new Date().toLocaleDateString("en-CA");
  const [filtros, setFiltros] = useState({
    dataInicial: dataHojeInput,
    dataFinal: dataHojeInput,
    apenasAbertos: false,
  });

  // Funções de paginação
  const totalPages = Math.ceil(caixas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCaixas = caixas.slice(startIndex, endIndex);

  const mostrarObservacao = (observacao) => {
    setObservacaoView(observacao || "Nenhuma observação");
    setShowObservacao(true);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // Função para verificar se o usuário é recente (criado nos últimos 6 dias)
  const isUsuarioRecente = () => {
    if (!usuario?.createdAt) {
      return false;
    }

    const dataCreacaoStr = usuario.createdAt.split(" ")[0]; // "02/09/2025"
    const [dia, mes, ano] = dataCreacaoStr.split("/");
    const dataCreacao = new Date(ano, mes - 1, dia);

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    dataCreacao.setHours(0, 0, 0, 0);

    const diferencaEmMs = hoje - dataCreacao;
    const diferencaEmDias = Math.floor(diferencaEmMs / (1000 * 60 * 60 * 24));

    return diferencaEmDias <= 6;
  };

  const loadTiposCaixa = async () => {
    try {
      const tiposCaixaEncontrados = await tipoCaixaService.listarTipoCaixa();

      const tiposCaixaFormatados = tiposCaixaEncontrados.map((tipo) => ({
        tipoCaixa: {
          id: tipo.id,
          nome: tipo.nome,
          ordem: tipo.ordem,
        },
        valorMovimento: 0,
        readonly: true,
      }));

      setTiposCaixa(tiposCaixaFormatados);
    } catch (err) {
      setTiposCaixa([]);
    }
  };

  const loadCaixas = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (filtros.apenasAbertos) {
        data = await caixaService.listarCaixasAbertos();
      } else if (filtros.dataInicial === filtros.dataFinal) {
        data = await caixaService.listarCaixaPorData(filtros.dataInicial);
        data = data ? [data] : [];
      } else {
        data = await caixaService.listarCaixasPorPeriodo(
          filtros.dataInicial,
          filtros.dataFinal
        );
      }
      setCaixas(Array.isArray(data) ? data : []);
    } catch (err) {
      setCaixas([]);
      if (!err.message.includes("Unexpected end of JSON input")) {
      }
    } finally {
      setLoading(false);
    }
  }, [filtros.apenasAbertos, filtros.dataFinal, filtros.dataInicial]);

  const loadFormasPagamento = async () => {
    try {
      const data = await formaPagamentoService.listarFormasPagamento();
      // "Pix App" é lançado só pela integração automática com o Sisodonto -
      // não aparece aqui pro usuário lançar manualmente.
      setFormasPagamento(
        data.filter((forma) => forma.nome?.trim().toLowerCase() !== "pix app")
      );
    } catch (err) {}
  };

  const abrirImportacaoForm = () => {
    setImportacaoTipoCaixaId("");
    setImportacaoArquivo(null);
    setShowImportacaoForm(true);
  };

  const cancelarImportacao = () => {
    setShowImportacaoForm(false);
    setImportacaoTipoCaixaId("");
    setImportacaoArquivo(null);
  };

  const importarCaixas = async () => {
    if (!importacaoTipoCaixaId) {
      showError("Selecione o tipo de caixa da importação.");
      return;
    }
    if (!importacaoArquivo) {
      showError("Selecione um arquivo CSV ou XLSX.");
      return;
    }

    setLoading(true);
    try {
      const resumo = await caixaService.importarArquivo(
        importacaoArquivo,
        importacaoTipoCaixaId
      );
      showSuccess(
        `Importação concluída: ${resumo.criados || 0} caixa(s) criado(s) e ${
          resumo.ignorados || 0
        } ignorado(s).`
      );
      cancelarImportacao();
      await loadCaixas();
    } catch (err) {
      showError(err.message || "Erro ao importar caixas");
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const aplicarFiltros = () => {
    loadCaixas();
  };

  const solicitarAberturaCaixa = async () => {
    if (!solicitacaoAberturaCaixa.motivo.trim()) {
      showError("Motivo é obrigatório para solicitar abertura de caixa");
      return;
    }

    setLoading(true);
    try {
      const solicitacaoData = {
        motivo: solicitacaoAberturaCaixa.motivo,
        datasCaixaSemLancamento: datasImpedimento,
      };

      await solicitacaoAberturaCaixaService.solicitarAberturaCaixa(
        solicitacaoData
      );

      showSuccess("Solicitação de abertura de caixa enviada com sucesso!");

      setShowSolicitacaoAberturaForm(false);
      setDatasImpedimento([]);
      setSolicitacaoAberturaCaixa({ motivo: "" });

      setTimeout(() => {
        loadCaixas();
      }, 500);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  // Função modificada para abrir caixa
  const abrirCaixa = async (dataFinal = null) => {
    setLoading(true);
    try {
      const novoCaixa = await caixaService.abrirCaixa(dataFinal);

      showSuccess("Caixa aberto com sucesso!");
      await loadCaixas();
      if (novoCaixa && novoCaixa.id) {
        abrirMovimentoForm(novoCaixa);
      }
    } catch (err) {
      // Verificar se é erro 400 (Bad Request) - que geralmente é erro de validação/negócio
      if (
        err.message &&
        (err.message.includes("HTTP 400") ||
          err.message.includes(
            "Existe data(s) sem lançar caixa(s), lance primeiro nessa(s) data(s)"
          ))
      ) {
        // Extrair as datas da mensagem (mais flexível)
        const datasRegex = /(\d{4}-\d{2}-\d{2})/g;
        const datasEncontradas = err.message.match(datasRegex) || [];

        if (datasEncontradas.length > 0) {
          // Armazenar as datas no formato original (YYYY-MM-DD) para envio ao servidor
          // e também no formato brasileiro (DD/MM/YYYY) para exibição
          setDatasImpedimento(
            datasEncontradas.map((data) => {
              const [ano, mes, dia] = data.split("-");
              return `${dia}/${mes}/${ano}`;
            })
          );

          setSolicitacaoAberturaCaixa({ motivo: "" });
          setShowSolicitacaoAberturaForm(true);
        } else {
          // Se não encontrou datas, mas é erro de negócio, abrir modal mesmo assim
          setDatasImpedimento([]);
          setSolicitacaoAberturaCaixa({ motivo: "" });
          setShowSolicitacaoAberturaForm(true);
        }
      } else {
        showError(err.message || "Erro ao abrir caixa");
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para lidar com o clique no botão "Abrir Caixa"
  const handleAbrirCaixa = () => {
    if (isAdmin() || isUsuarioRecente()) {
      setDataCaixaSelecionada(dataHojeInput);
      setShowDataCaixaForm(true);
    } else {
      abrirCaixa();
    }
  };

  // Função para confirmar abertura com data selecionada
  const confirmarAberturaCaixa = () => {
    if (isDataFutura(dataCaixaSelecionada)) {
      showError(
        "Não é permitido abrir caixa com data futura. Selecione uma data até hoje."
      );
      return;
    }

    abrirCaixa(dataCaixaSelecionada);
    setShowDataCaixaForm(false);
  };

  // Função para cancelar seleção de data
  const cancelarSelecaoData = () => {
    setShowDataCaixaForm(false);
    setDataCaixaSelecionada(dataHojeInput);
  };

  // Remover os try/catch específicos para JSON e deixar o fetchWithAuth lidar com isso
  const solicitarReabertura = async () => {
    if (!selectedCaixa || !reaberturaData.motivo.trim()) {
      showError("Motivo é obrigatório para solicitar reabertura");
      return;
    }

    setLoading(true);
    try {
      const solicitacaoData = {
        motivo: reaberturaData.motivo,
        caixa: {
          id: selectedCaixa.id,
        },
      };

      await solicitacaoReaberturaCaixaService.solicitarReaberturaCaixa(
        solicitacaoData
      );
      showSuccess("Solicitação de reabertura enviada com sucesso!");
      setShowReaberturaForm(false);
      loadCaixas();
    } catch (err) {
      showError(err.message || "Erro ao solicitar reabertura");
    } finally {
      setLoading(false);
    }
  };

  const cancelarSolicitacaoAbertura = () => {
    setShowSolicitacaoAberturaForm(false);
    setDatasImpedimento([]);
    setSolicitacaoAberturaCaixa({ motivo: "" });
  };

  const fecharCaixa = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description:
        "Ao salvar você já fechará este caixa. Tem certeza que deseja fechar este caixa?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await caixaService.fecharCaixa(id);
      showSuccess("Caixa fechado com sucesso!");
      loadCaixas();
    } catch (err) {
      showError(err.message || "Erro ao fechar caixa");
    } finally {
      setLoading(false);
    }
  };

  const deletarCaixa = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este caixa?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await caixaService.deletarCaixa(id);
      showSuccess("Caixa excluído com sucesso!");
      loadCaixas();
    } catch (err) {
      showError(err.message || "Erro ao excluir caixa");
    } finally {
      setLoading(false);
    }
  };

  const abrirMovimentoForm = (caixa) => {
    setSelectedCaixa(caixa);

    // Preparar tipos de caixa - fazer deep copy para evitar mutação do estado original
    const tiposCaixaParaForm = tiposCaixa.map(tipo => ({
      tipoCaixa: {
        id: tipo.tipoCaixa.id,
        nome: tipo.tipoCaixa.nome,
        ordem: tipo.tipoCaixa.ordem,
      },
      valorMovimento: 0,
      readonly: tipo.readonly,
    }));

    if (caixa.caixaMovimentoTipoCaixa?.length > 0) {
      tiposCaixaParaForm.forEach((tipoCaixaItem) => {
        const movimentoExistente = caixa.caixaMovimentoTipoCaixa.find(
          (mov) => mov.tipoCaixa.id === tipoCaixaItem.tipoCaixa.id
        );
        if (movimentoExistente) {
          tipoCaixaItem.valorMovimento = movimentoExistente.valorMovimento;
        }
      });
    }

    // Preparar formas de pagamento
    let formasMovimento = [];

    if (caixa.caixaMovimento?.length > 0) {
      formasMovimento = formasPagamento.map((forma) => {
        const movimentoExistente = caixa.caixaMovimento.find(
          (mov) => mov.formaPagamento.id === forma.id
        );
        return {
          formaPagamento: { id: forma.id, nome: forma.nome },
          valorMovimento: movimentoExistente
            ? movimentoExistente.valorMovimento
            : 0,
        };
      });
    } else {
      formasMovimento = formasPagamento.map((forma) => ({
        formaPagamento: { id: forma.id, nome: forma.nome },
        valorMovimento: 0,
      }));
    }

    // Preparar valores de display - apenas com valores existentes ou vazio
    const displayTipoCaixa = {};
    const displayFormasPagamento = {};

    tiposCaixaParaForm.forEach((tipo, index) => {
      if (tipo.valorMovimento > 0) {
        const valorFormatado = formatarValorReal(
          (tipo.valorMovimento * 100).toString()
        );
        displayTipoCaixa[index] = valorFormatado.formatted;
      } else {
        displayTipoCaixa[index] = ""; // Campo vazio para mostrar placeholder
      }
    });

    formasMovimento.forEach((forma, index) => {
      if (forma.valorMovimento > 0) {
        const valorFormatado = formatarValorReal(
          (forma.valorMovimento * 100).toString()
        );
        displayFormasPagamento[index] = valorFormatado.formatted;
      } else {
        displayFormasPagamento[index] = ""; // Campo vazio para mostrar placeholder
      }
    });

    let valorRetiradaDisplay = "";
    if (caixa.valorRetirada > 0) {
      const valorRetiradaFormatado = formatarValorReal(
        (caixa.valorRetirada * 100).toString()
      );
      valorRetiradaDisplay = valorRetiradaFormatado.formatted;
    }

    let valorSangriaDisplay = "";
    if (caixa.valorSangria > 0) {
      const valorSangriaFormatado = formatarValorReal(
        (caixa.valorSangria * 100).toString()
      );
      valorSangriaDisplay = valorSangriaFormatado.formatted;
    }

    // O fundo "contado" guardado é o implícito (total contado − dinheiro
    // recebido), não o total físico. Pra repopular o total físico que a
    // pessoa realmente digitou, soma esse fundo de volta com o dinheiro
    // recebido que já está sendo restaurado em formasMovimento.
    const dinheiroCarregado =
      formasMovimento.find((mov) =>
        mov.formaPagamento.nome.toLowerCase().includes("dinheiro")
      )?.valorMovimento || 0;
    const fundoImplicitoInicial =
      caixa.valorFundoContado == null ? FUNDO_CAIXA_PADRAO : caixa.valorFundoContado;
    const totalDinheiroContadoInicial = fundoImplicitoInicial + dinheiroCarregado;
    const totalDinheiroContadoDisplay = formatarValorReal(
      (totalDinheiroContadoInicial * 100).toString()
    ).formatted;

    setDisplayValues({
      valorRetirada: valorRetiradaDisplay,
      valorSangria: valorSangriaDisplay,
      totalDinheiroContado: totalDinheiroContadoDisplay,
      tipoCaixa: displayTipoCaixa,
      formasPagamento: displayFormasPagamento,
    });
    // Se já tinha uma quebra real registrada, trava o valor reconstruído
    // como "manual" — senão o efeito de auto-preenchimento reescreve por
    // cima assim que o formulário monta (assumindo "sem quebra" de novo).
    const fundoPersistidoDivergente =
      caixa.valorFundoContado != null &&
      Math.round((caixa.valorFundoContado - FUNDO_CAIXA_PADRAO) * 100) !== 0;
    setTotalDinheiroContadoManual(fundoPersistidoDivergente);

    const { motivoQuebraFundo, motivoQuebraCaixa, observacao } =
      parseObservacaoSalva(caixa.observacao);

    setMovimentoData({
      caixaMovimento: formasMovimento,
      caixaMovimentoTipoCaixa: tiposCaixaParaForm,
      valorRetirada: caixa.valorRetirada || 0,
      valorSangria: caixa.valorSangria || 0,
      valorQuebra: caixa.valorQuebra || 0,
      motivoQuebraCaixa,
      totalDinheiroContado: totalDinheiroContadoInicial,
      valorQuebraFundo: caixa.valorQuebraFundo || 0,
      motivoQuebraFundo,
      observacao,
    });

    setShowMovimentoForm(true);
  };

  const calcularQuebra = useCallback(() => {
    const totalTiposCaixa = movimentoData.caixaMovimentoTipoCaixa.reduce(
      (sum, item) => sum + (item.valorMovimento || 0),
      0
    );

    const totalFormasPagamento = movimentoData.caixaMovimento.reduce(
      (sum, item) => sum + (item.valorMovimento || 0),
      0
    );

    const sangria = movimentoData.valorSangria || 0;
    const retirada = movimentoData.valorRetirada || 0;

    const totalAjustado = totalFormasPagamento + sangria + retirada;

    const quebra = totalAjustado - totalTiposCaixa;

    setMovimentoData((prev) => ({
      ...prev,
      valorQuebra: quebra,
    }));
  }, [
    movimentoData.caixaMovimento,
    movimentoData.caixaMovimentoTipoCaixa,
    movimentoData.valorRetirada,
    movimentoData.valorSangria,
  ]);

  // Valor que o usuário digitou na forma de pagamento "Dinheiro" — não dá
  // pra contar o fundo isolado do resto (dinheiro não vem rotulado por
  // origem), então esse é o dinheiro recebido no dia que precisa ser
  // descontado do total físico contado pra saber quanto sobrou de fundo.
  const getValorDinheiroDigitado = () => {
    const movimentoDinheiro = movimentoData.caixaMovimento.find((mov) =>
      mov.formaPagamento.nome.toLowerCase().includes("dinheiro")
    );
    return movimentoDinheiro?.valorMovimento || 0;
  };

  const calcularQuebraFundo = useCallback(() => {
    const totalContado = movimentoData.totalDinheiroContado || 0;
    const dinheiroRecebido = getValorDinheiroDigitado();
    const fundoImplicito = totalContado - dinheiroRecebido;
    const quebraFundo = fundoImplicito - FUNDO_CAIXA_PADRAO;

    setMovimentoData((prev) => ({
      ...prev,
      valorQuebraFundo: quebraFundo,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movimentoData.totalDinheiroContado, movimentoData.caixaMovimento]);

  // Enquanto a pessoa não editar o total contado na mão, ele se atualiza
  // sozinho pra sempre refletir "fundo intacto" (100 + o que foi digitado em
  // Dinheiro) — assim a quebra só aparece quando ela realmente recontar e
  // achar uma diferença de verdade.
  useEffect(() => {
    if (totalDinheiroContadoManual) {
      return;
    }

    const dinheiroRecebido = getValorDinheiroDigitado();
    const totalSugerido = FUNDO_CAIXA_PADRAO + dinheiroRecebido;

    setMovimentoData((prev) => ({
      ...prev,
      totalDinheiroContado: totalSugerido,
    }));
    setDisplayValues((prev) => ({
      ...prev,
      totalDinheiroContado: formatarValorReal(
        (totalSugerido * 100).toString()
      ).formatted,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movimentoData.caixaMovimento, totalDinheiroContadoManual]);

  const handleValorChange = (tipo, index, valor) => {
    const { formatted, numeric } = formatarValorReal(valor);

    // Atualizar valores de display
    setDisplayValues((prev) => {
      const novo = { ...prev };

      if (tipo === "valorRetirada") {
        novo[tipo] = formatted;
      } else if (tipo === "valorSangria") {
        novo[tipo] = formatted;
      } else if (tipo === "totalDinheiroContado") {
        novo[tipo] = formatted;
      } else if (tipo === "tipoCaixa") {
        novo.tipoCaixa[index] = formatted;
      } else if (tipo === "formasPagamento") {
        novo.formasPagamento[index] = formatted;
      }

      return novo;
    });

    // Atualizar valores numéricos
    if (tipo === "valorRetirada") {
      setMovimentoData((prev) => ({
        ...prev,
        valorRetirada: numeric,
      }));
    } else if (tipo === "valorSangria") {
      setMovimentoData((prev) => ({
        ...prev,
        valorSangria: numeric,
      }));
    } else if (tipo === "totalDinheiroContado") {
      setTotalDinheiroContadoManual(true);
      setMovimentoData((prev) => ({
        ...prev,
        totalDinheiroContado: numeric,
      }));
    } else if (tipo === "tipoCaixa") {
      updateMovimentoTipoCaixa(index, numeric);
    } else if (tipo === "formasPagamento") {
      updateMovimentoFormaPagamento(index, "valorMovimento", numeric);
    }
  };

  const updateMovimentoFormaPagamento = (index, field, value) => {
    if (field === "valorMovimento") {
      setMovimentoData((prev) => ({
        ...prev,
        caixaMovimento: prev.caixaMovimento.map((item, i) =>
          i === index ? { ...item, valorMovimento: value } : item
        ),
      }));
    } else {
      setMovimentoData((prev) => ({
        ...prev,
        caixaMovimento: prev.caixaMovimento.map((item, i) =>
          i === index
            ? field === "formaPagamento"
              ? {
                  ...item,
                  formaPagamento: formasPagamento.find(
                    (f) => f.id === value
                  ) || {
                    id: value,
                    nome: "",
                  },
                }
              : { ...item, [field]: value }
            : item
        ),
      }));
    }
  };

  const updateMovimentoTipoCaixa = (index, value) => {
    setMovimentoData((prev) => ({
      ...prev,
      caixaMovimentoTipoCaixa: prev.caixaMovimentoTipoCaixa.map((item, i) =>
        i === index ? { ...item, valorMovimento: value } : item
      ),
    }));
  };

  const salvarMovimento = async () => {
    if (!selectedCaixa) return;

    const houveQuebraFundo =
      Math.round((movimentoData.valorQuebraFundo || 0) * 100) !== 0;

    if (houveQuebraFundo && !movimentoData.motivoQuebraFundo?.trim()) {
      showError(
        "Informe o motivo da quebra do fundo de caixa antes de fechar o caixa."
      );
      return;
    }

    const houveQuebraCaixa =
      Math.round((movimentoData.valorQuebra || 0) * 100) !== 0;

    if (houveQuebraCaixa && !movimentoData.motivoQuebraCaixa?.trim()) {
      showError(
        "Informe o motivo da quebra de caixa antes de fechar o caixa."
      );
      return;
    }

    const houveRetirada =
      Math.round((movimentoData.valorRetirada || 0) * 100) > 0;

    if (houveRetirada && !movimentoData.observacao?.trim()) {
      showError(
        "Informe a observação explicando a saída de caixa antes de fechar o caixa."
      );
      return;
    }

    const tiposCaixaValidos = movimentoData.caixaMovimentoTipoCaixa
      .filter((item) => {
        return (
          item.tipoCaixa.id &&
          typeof item.tipoCaixa.id === "string" &&
          item.tipoCaixa.id.length === 36 &&
          item.valorMovimento > 0
        );
      })
      .map((item) => ({
        tipoCaixa: {
          id: item.tipoCaixa.id,
        },
        valorMovimento: item.valorMovimento,
      }));

    const formasPagamentoValidas = movimentoData.caixaMovimento
      .filter((item) => {
        return (
          item.formaPagamento.id &&
          typeof item.formaPagamento.id === "string" &&
          item.formaPagamento.id.length === 36 &&
          item.valorMovimento > 0
        );
      })
      .map((item) => ({
        formaPagamento: {
          id: item.formaPagamento.id,
        },
        valorMovimento: item.valorMovimento,
      }));

    const dadosParaEnvio = {
      caixaMovimentoTipoCaixa: tiposCaixaValidos,
      caixaMovimento: formasPagamentoValidas,
      valorRetirada: movimentoData.valorRetirada || 0,
      valorSangria: movimentoData.valorSangria || 0,
      valorQuebra: movimentoData.valorQuebra || 0,
      valorQuebraFundo: movimentoData.valorQuebraFundo || 0,
      // Guarda o fundo implícito (total contado - dinheiro recebido), não o
      // total físico digitado — mantém o significado do campo no backend.
      valorFundoContado:
        (movimentoData.totalDinheiroContado || 0) - getValorDinheiroDigitado(),
      observacao: montarObservacaoFinal(
        movimentoData.motivoQuebraFundo,
        movimentoData.motivoQuebraCaixa,
        movimentoData.observacao
      ),
    };

    setLoading(true);
    try {
      await caixaService.adicionarMovimento(selectedCaixa.id, dadosParaEnvio);
      showSuccess("Movimento adicionado com sucesso!");
      setShowMovimentoForm(false);
      loadCaixas();
    } catch (err) {
      showError(err.message || "Erro ao salvar movimento");
    } finally {
      setLoading(false);
    }
    fecharCaixa(selectedCaixa.id);
  };

  const cancelarMovimento = () => {
    setShowMovimentoForm(false);
    setSelectedCaixa(null);

    setDisplayValues({
      valorRetirada: "", // Campo vazio para mostrar placeholder
      valorSangria: "",
      totalDinheiroContado: formatarValorReal(
        (FUNDO_CAIXA_PADRAO * 100).toString()
      ).formatted,
      tipoCaixa: {},
      formasPagamento: {},
    });
    setTotalDinheiroContadoManual(false);

    setMovimentoData({
      caixaMovimento: [
        { formaPagamento: { id: "", nome: "" }, valorMovimento: 0 },
      ],
      caixaMovimentoTipoCaixa: tiposCaixa.map(tipo => ({
        tipoCaixa: {
          id: tipo.tipoCaixa.id,
          nome: tipo.tipoCaixa.nome,
          ordem: tipo.tipoCaixa.ordem,
        },
        valorMovimento: 0,
        readonly: tipo.readonly,
      })),
      valorRetirada: 0,
      valorSangria: 0,
      valorQuebra: 0,
      motivoQuebraCaixa: "",
      totalDinheiroContado: FUNDO_CAIXA_PADRAO,
      valorQuebraFundo: 0,
      motivoQuebraFundo: "",
      observacao: "",
    });
  };

  const isAdmin = () => {
    return authService.hasRole(
      usuario,
      "admin",
      authService.getSessaoOrganizacao()
    );
  };

  const isDataFutura = (dataCaixa) => {
    if (!dataCaixa) {
      return false;
    }

    return dataCaixa > dataHojeInput;
  };

  const isDataHoje = (dataCaixa) => {
    const hoje = formatarData(new Date().toLocaleDateString("en-CA"));
    return dataCaixa === hoje;
  };

  const reabrirCaixaDiretamente = async (caixa) => {
    setLoading(true);
    try {
      const solicitacaoData = {
        caixa: {
          id: caixa.id,
        },
      };

      try {
        await solicitacaoReaberturaCaixaService.solicitarReaberturaCaixa(
          solicitacaoData
        );
      } catch (error) {
        if (
          error.message &&
          error.message.includes("Unexpected end of JSON input")
        ) {
        } else {
          throw error;
        }
      }

      showSuccess("Caixa reaberto com sucesso!");

      setTimeout(() => {
        loadCaixas();
      }, 500);
    } catch (err) {
      if (err.message && err.message.includes("Unexpected end of JSON input")) {
        showSuccess("Processando reabertura do caixa...");
        setTimeout(() => {
          loadCaixas();
        }, 1000);
      } else {
        showError(err.message || "Erro ao reabrir caixa");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReabertura = (caixa) => {
    if (isAdmin() || isDataHoje(caixa.data)) {
      reabrirCaixaDiretamente(caixa);
    } else {
      abrirReaberturaForm(caixa);
    }
  };

  const gerarPdfEnvelope = async (caixa) => {
    setLoading(true);
    try {
      const blob = await caixaService.gerarPdfEnvelopeCaixa(caixa.id);
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `envelope-caixa-${formatarData(caixa.data)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('PDF gerado com sucesso!');
    } catch (err) {
      showError(err.message || 'Erro ao gerar PDF do envelope');
    } finally {
      setLoading(false);
    }
  };

  const abrirReaberturaForm = (caixa) => {
    setSelectedCaixa(caixa);
    setReaberturaData({ motivo: "" });
    setShowReaberturaForm(true);
  };

  const cancelarReabertura = () => {
    setShowReaberturaForm(false);
    setSelectedCaixa(null);
    setReaberturaData({ motivo: "" });
  };

  const handleKeyDown = (e, currentField) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();

      const direction = e.shiftKey && e.key === "Tab" ? -1 : 1;

      // Lista de campos na ordem de navegação
      const fieldOrder = [
        // Tipos de Caixa
        ...movimentoData.caixaMovimentoTipoCaixa.map(
          (_, index) => `tipoCaixa-${index}`
        ),
        // Formas de Pagamento
        ...movimentoData.caixaMovimento.map(
          (_, index) => `formaPagamento-${index}`
        ),
        // Outros campos
        "valorRetirada",
        "valorSangria",
        "motivoQuebraCaixa",
        "totalDinheiroContado",
        "motivoQuebraFundo",
        "observacao",
      ];

      const currentIndex = fieldOrder.indexOf(currentField);
      if (currentIndex === -1) {
        return;
      }

      let nextIndex = currentIndex + direction;

      // Se chegou ao final, volta para o primeiro
      if (nextIndex >= fieldOrder.length) {
        nextIndex = 0;
      } else if (nextIndex < 0) {
        nextIndex = fieldOrder.length - 1;
      }

      // Procura o próximo campo disponível (não readonly/disabled)
      let attempts = 0;
      while (attempts < fieldOrder.length) {
        const nextField = fieldOrder[nextIndex];
        const nextInput = inputRefs.current[nextField];

        if (nextInput && !nextInput.readOnly && !nextInput.disabled) {
          nextInput.focus();
          nextInput.select(); // Seleciona todo o texto para facilitar a edição
          break;
        }

        nextIndex = (nextIndex + 1) % fieldOrder.length;
        attempts++;
      }
    }
  };

  // Função para registrar refs dos inputs
  const setInputRef = (fieldName, element) => {
    if (element) {
      inputRefs.current[fieldName] = element;
    }
  };

  // Função para calcular totais de um caixa
  const calcularTotaisCaixa = (caixa) => {
    // Processar formas de pagamento
    let formasPagamentoProcessadas = [...(caixa.caixaMovimento || [])];

    // Verificar se existe forma de pagamento "dinheiro" e se há sangria
    const sangria = caixa.sangria || 0;

    if (sangria > 0) {
      const indexDinheiro = formasPagamentoProcessadas.findIndex((mov) =>
        mov.formaPagamento.nome.toLowerCase().includes("dinheiro")
      );

      if (indexDinheiro !== -1) {
        // Se encontrou dinheiro, soma a sangria com o valor do dinheiro
        formasPagamentoProcessadas = formasPagamentoProcessadas.map(
          (mov, index) => {
            if (index === indexDinheiro) {
              return {
                ...mov,
                valorMovimento: (mov.valorMovimento || 0) + sangria,
              };
            }
            return mov;
          }
        );
      } else {
        // Se não encontrou dinheiro, adiciona como nova forma de pagamento
        formasPagamentoProcessadas.push({
          formaPagamento: { nome: "Dinheiro (Sangria)" },
          valorMovimento: sangria,
        });
      }
    }

    // Filtrar apenas formas de pagamento com valor maior que zero
    const formasPagamentoNaoZeradas = formasPagamentoProcessadas.filter(
      (mov) => (mov.valorMovimento || 0) > 0
    );

    // Filtrar e ordenar os tipos de caixa
    const tiposCaixaComValor = (caixa.caixaMovimentoTipoCaixa || []).filter(
      (mov) => (mov.valorMovimento || 0) > 0
    );

    // Recalcular total das formas de pagamento (já inclui a sangria somada ao dinheiro)
    const totalFormasPagamentoAtualizado = formasPagamentoNaoZeradas.reduce(
      (sum, item) => sum + (item.valorMovimento || 0),
      0
    );

    return {
      totalFormasPagamento: totalFormasPagamentoAtualizado,
      quantidadeCaixa: 1,
      sangrias: sangria,
      retiradas: caixa.valorRetirada || 0,
      quebras: caixa.valorQuebra || 0,
      formasPagamento: formasPagamentoNaoZeradas,
      tiposCaixa: tiposCaixaComValor,
    };
  };

  // Reset página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [caixas.length]);

  useEffect(() => {
    loadCaixas();
    loadFormasPagamento();
    loadTiposCaixa();
  }, [loadCaixas]);

  useEffect(() => {
    calcularQuebra();
  }, [
    movimentoData.caixaMovimentoTipoCaixa,
    movimentoData.caixaMovimento,
    movimentoData.valorRetirada,
    calcularQuebra,
  ]);

  useEffect(() => {
    calcularQuebraFundo();
  }, [
    movimentoData.totalDinheiroContado,
    movimentoData.caixaMovimento,
    calcularQuebraFundo,
  ]);

  // Componente de Paginação
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const getVisiblePages = () => {
      const pages = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= maxVisiblePages; i++) {
            pages.push(i);
          }
        } else if (currentPage >= totalPages - 2) {
          for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          for (let i = currentPage - 2; i <= currentPage + 2; i++) {
            pages.push(i);
          }
        }
      }

      return pages;
    };

    return (
      <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
        <div className="text-sm text-gray-700">
          Mostrando {startIndex + 1} a {Math.min(endIndex, caixas.length)} de{" "}
          {caixas.length} caixas
        </div>

        <div className="flex items-center gap-2">
          <IconButton
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            icon={ChevronLeft}
            label="Página anterior"
          />

          {getVisiblePages().map((page) => (
            <Button
              key={page}
              onClick={() => goToPage(page)}
              variant="ghost"
              size="sm"
              className={
                currentPage === page
                  ? buttonPageActiveClass
                  : buttonPageClass
              }
            >
              {page}
            </Button>
          ))}

          <IconButton
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            icon={ChevronRight}
            label="Próxima página"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageContainer className="py-6 px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Caixa Diário
          </h1>
          <p className="text-gray-600">
            Gerencie seus caixas diários e movimentações
          </p>
        </div>

        {/* Filtros e Ações */}
        <div className={`${cardClass} mb-6 p-4`}>
          <div className="grid grid-cols-1 gap-4 items-end md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_minmax(0,auto)]">
            <FormField label="Data Inicial" htmlFor="filtro-data-inicial">
              <TextInput
                id="filtro-data-inicial"
                type="date"
                name="dataInicial"
                value={filtros.dataInicial}
                onChange={handleFiltroChange}
              />
            </FormField>

            <FormField label="Data Final" htmlFor="filtro-data-final">
              <TextInput
                id="filtro-data-final"
                type="date"
                name="dataFinal"
                value={filtros.dataFinal}
                onChange={handleFiltroChange}
              />
            </FormField>

            <div className="flex min-h-10 items-center self-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="apenasAbertos"
                  checked={filtros.apenasAbertos}
                  onChange={handleFiltroChange}
                  className={checkboxClass}
                />
                <span className="text-sm font-medium text-gray-700">
                  Apenas abertos
                </span>
              </label>
            </div>

            <div className="flex min-w-0 flex-wrap justify-end gap-2">
              <Button
                onClick={aplicarFiltros}
                disabled={loading}
                icon={Calendar}
                className="whitespace-nowrap"
              >
                Filtrar
              </Button>

              <Button
                onClick={handleAbrirCaixa}
                disabled={loading}
                variant="success"
                icon={Plus}
                className="whitespace-nowrap"
              >
                Abrir Caixa
              </Button>

              <Button
                onClick={abrirImportacaoForm}
                disabled={loading}
                variant="secondary"
                icon={Upload}
                className="whitespace-nowrap"
              >
                Importar Caixa
              </Button>
            </div>
          </div>
        </div>

        {/* Lista de Caixas em Cards */}
        <div className={cardClass}>
          <div className={cardHeaderClass}>
            <h3 className={cardHeaderTitleClass}>Meus Caixas</h3>
          </div>

          {loading && caixas.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Carregando caixas...
            </div>
          ) : caixas.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Nenhum caixa encontrado para o período selecionado
            </div>
          ) : (
            <>
              <div className={tableWrapperClass}>
                <table className={tableClass}>
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className={tableHeaderCellClass}>
                        Data
                      </th>
                      <th className={tableHeaderCellClass}>
                        Usuário
                      </th>
                      <th className={tableHeaderCellClass}>
                        Status
                      </th>
                      <th className={tableHeaderCellClass}>
                        Valor Total
                      </th>
                      <th className={tableHeaderCellClass}>
                        Sangria de Caixa
                      </th>
                      <th className={tableHeaderCellClass}>
                        Saída de Caixa
                      </th>
                      <th className={tableHeaderCellClass}>
                        Quebra
                      </th>
                      <th className={tableHeaderCellClass}>
                        Quebra do Fundo
                      </th>
                      <th className={tableHeaderCellClass}>
                        Formas Pagamento
                      </th>
                      <th className={tableHeaderCellCenterClass}>
                        OBS
                      </th>
                      <th className={tableHeaderCellRightClass}>
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className={tableBodyClass}>
                    {currentCaixas.map((caixa) => {
                      const totais = calcularTotaisCaixa(caixa);

                      return (
                        <React.Fragment key={caixa.id}>
                          <tr className={tableRowClass}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatarData(caixa.data)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {caixa.usuario?.nome ||
                                  "Usuário não identificado"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                  caixa.aberto
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {caixa.aberto ? (
                                  <>
                                    <Unlock className="w-3 h-3 mr-1" />
                                    Aberto
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3 h-3 mr-1" />
                                    Fechado
                                  </>
                                )}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrencyMoeda(caixa.valorTotal)}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-yellow-600 font-medium">
                                {formatCurrencyMoeda(caixa.valorSangria)}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-yellow-600 font-medium">
                                {formatCurrencyMoeda(caixa.valorRetirada)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div
                                className={`text-sm font-medium ${
                                  caixa.valorQuebra < 0
                                    ? "text-red-600"
                                    : caixa.valorQuebra > 0
                                    ? "text-yellow-600"
                                    : "text-green-600"
                                }`}
                              >
                                {formatCurrencyMoeda(caixa.valorQuebra)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div
                                className={`text-sm font-medium ${
                                  caixa.valorQuebraFundo < 0
                                    ? "text-red-600"
                                    : caixa.valorQuebraFundo > 0
                                    ? "text-yellow-600"
                                    : "text-green-600"
                                }`}
                              >
                                {formatCurrencyMoeda(caixa.valorQuebraFundo)}
                              </div>
                            </td>
                            <td className="px-6 py-2">
                              <div className="text-sm text-gray-900">
                                {totais.formasPagamento.length > 0 ? (
                                  <div className="space-y-1">
                                    {totais.formasPagamento.map(
                                      (forma, index) => (
                                        <div
                                          key={index}
                                          className="flex justify-between text-xs"
                                        >
                                          <span className="truncate max-w-20">
                                            {forma.formaPagamento.nome}
                                          </span>
                                          <span className="font-medium">
                                            {formatCurrencyMoeda(
                                              forma.valorMovimento
                                            )}
                                          </span>
                                        </div>
                                      )
                                    )}
                                    <div className="border-t pt-1 flex justify-between text-xs font-semibold">
                                      <span>Total:</span>
                                      <span>
                                        {formatCurrencyMoeda(
                                          totais.totalFormasPagamento
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    Nenhuma forma
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {caixa.observacao && caixa.observacao.trim() ? (
                                <button
                                  onClick={() =>
                                    mostrarObservacao(caixa.observacao)
                                  }
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors flex items-center justify-center gap-1 mx-auto"
                                  title="Ver observação"
                                >
                                  <Eye size={14} />
                                </button>
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  Sem observação
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                {caixa.aberto ? (
                                  <>
                                    <IconButton
                                      onClick={() => abrirMovimentoForm(caixa)}
                                      disabled={loading}
                                      icon={Receipt}
                                      variant="info"
                                      label="Adicionar Movimento"
                                    />

                                    <IconButton
                                      onClick={() => fecharCaixa(caixa.id)}
                                      disabled={loading}
                                      icon={Lock}
                                      variant="warning"
                                      label="Fechar Caixa"
                                    />

                                    <IconButton
                                      onClick={() => deletarCaixa(caixa.id)}
                                      disabled={
                                        loading ||
                                        (caixa.caixaMovimento &&
                                          caixa.caixaMovimento.length > 0)
                                      }
                                      icon={Trash2}
                                      variant="danger"
                                      label="Excluir Caixa"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <IconButton
                                      onClick={() => handleReabertura(caixa)}
                                      disabled={loading}
                                      icon={Unlock}
                                      variant="warning"
                                      label={
                                        isAdmin() || isDataHoje(caixa.data)
                                          ? "Reabrir Caixa"
                                          : "Solicitar Reabertura"
                                      }
                                    />

                                    <IconButton
                                      onClick={() => gerarPdfEnvelope(caixa)}
                                      disabled={loading}
                                      icon={FileText}
                                      variant="danger"
                                      label="Gerar PDF do Envelope"
                                    />
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Linha dos tipos de caixa */}
                          <tr
                            className="border-b border-gray-200"
                            style={{ borderTop: "none" }}
                          >
                            <td colSpan="11" className="px-6 py-2 bg-white">
                              <div className="text-sm text-gray-900">
                                {totais.tiposCaixa.length > 0 ? (
                                  <div className="grid grid-cols-4 gap-4">
                                        {totais.tiposCaixa.map((tipo, index) => (
                                          <div
                                            key={index}
                                            className="border-2 border-blue-500 rounded px-3 py-2 text-center"
                                          >
                                            <div className="text-600 font-medium text-xs">
                                              {tipo.tipoCaixa.nome}
                                            </div>
                                            <div className="text-blue-600 font-semibold text-xs">
                                              {formatCurrencyMoeda(
                                                tipo.valorMovimento
                                              )}
                                            </div>
                                          </div>
                                        ))}

                                        <div className="border-2 border-blue-500 rounded px-3 py-2 text-center">
                                          <div className="text-600 font-medium text-xs">
                                            Total
                                          </div>
                                          <div className="text-blue-600 font-semibold text-xs">
                                            {formatCurrencyMoeda(
                                              totais.tiposCaixa.reduce(
                                                (sum, tipo) =>
                                                  sum + (tipo.valorMovimento || 0),
                                                0
                                              )
                                            )}
                                          </div>
                                        </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    Nenhum tipo de caixa
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Componente de Paginação */}
              <Pagination />
            </>
          )}
        </div>

        {/* Modal de Observação */}
        {showObservacao && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh]">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <MessageSquare size={20} />
                    Observação
                  </h3>
                  <button
                    onClick={() => setShowObservacao(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <p className="text-gray-700 whitespace-pre-wrap">{observacaoView}</p>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setShowObservacao(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal para seleção de data */}
        {showDataCaixaForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar size={20} className="text-blue-600" />
                  Selecionar Data do Caixa
                </h3>

                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Selecione a data que você deseja abrir o caixa.
                  </p>
                </div>

                <FormField label="Data do Caixa" required htmlFor="data-caixa">
                  <TextInput
                    id="data-caixa"
                    type="date"
                    value={dataCaixaSelecionada}
                    onChange={(e) => setDataCaixaSelecionada(e.target.value)}
                    max={dataHojeInput}
                  />
                </FormField>

                <div className="mt-4 flex flex-wrap justify-end gap-3 sm:gap-4">
                  <Button
                    onClick={cancelarSelecaoData}
                    variant="secondary"
                    icon={X}
                  >
                    Cancelar
                  </Button>

                  <Button
                    onClick={confirmarAberturaCaixa}
                    disabled={loading}
                    icon={Plus}
                  >
                    {loading ? "Abrindo..." : "Abrir Caixa"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showImportacaoForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Upload size={20} className="text-blue-600" />
                    Importar Caixa
                  </h3>
                  <IconButton
                    onClick={cancelarImportacao}
                    disabled={loading}
                    icon={X}
                    label="Fechar"
                  />
                </div>

                <div className="space-y-4">
                  <FormField
                    label="Tipo de Caixa"
                    required
                    htmlFor="importacao-tipo-caixa"
                  >
                    <TipoCaixaSelect
                      id="importacao-tipo-caixa"
                      tiposCaixa={tiposCaixa.map((item) => item.tipoCaixa)}
                      value={importacaoTipoCaixaId}
                      onChange={(e) => setImportacaoTipoCaixaId(e.target.value)}
                    />
                  </FormField>

                  <FormField label="Arquivo" required htmlFor="importacao-arquivo">
                    <FileInput
                      id="importacao-arquivo"
                      accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(e) => setImportacaoArquivo(e.target.files?.[0] || null)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Formatos aceitos: CSV ou XLSX.
                    </p>
                  </FormField>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    onClick={cancelarImportacao}
                    variant="secondary"
                    icon={X}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={importarCaixas}
                    disabled={loading}
                    icon={Upload}
                  >
                    {loading ? "Importando..." : "Importar"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showMovimentoForm && selectedCaixa && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Header fixo */}
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-semibold">
                  Movimentação do Caixa - {formatarData(selectedCaixa.data)}
                </h3>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {/* Tipos de Caixa */}
                <div className="mb-6">
                  {/* Cabeçalhos */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <h4 className="text-md font-medium">Tipos de Caixa</h4>
                    <h4 className="text-md font-medium">Valores</h4>
                  </div>

                  {/* Items */}
                  {movimentoData.caixaMovimentoTipoCaixa.map(
                    (movimento, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-2 gap-4 items-end mb-3 p-3 border rounded bg-gray-50"
                      >
                        <div>
                          <TextInput
                            type="text"
                            value={movimento.tipoCaixa.nome}
                            readOnly
                            tabIndex={-1}
                          />
                        </div>

                        <div>
                          <TextInput
                            ref={(el) => setInputRef(`tipoCaixa-${index}`, el)}
                            type="text"
                            value={displayValues.tipoCaixa[index] || ""}
                            onChange={(e) =>
                              handleValorChange(
                                "tipoCaixa",
                                index,
                                e.target.value
                              )
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, `tipoCaixa-${index}`)
                            }
                            placeholder="0,00"
                          />
                        </div>
                      </div>
                    )
                  )}

                  {tiposCaixa.length === 0 && (
                    <div className="text-center py-4 text-gray-500 border rounded-lg bg-gray-50">
                      Nenhum tipo de caixa cadastrado.
                    </div>
                  )}
                </div>

                {/* Formas de Pagamento */}
                <div className="mb-6">
                  <div className="grid grid-cols-2 mb-4">
                    <h4 className="text-md font-medium">
                      Formas de Pagamentos
                    </h4>
                    <h4 className="text-md font-medium">Valores</h4>
                    <p className="text-sm text-gray-600 col-span-2">
                      Preencha os valores para cada forma de pagamento
                    </p>
                  </div>

                  {movimentoData.caixaMovimento.map((movimento, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-2 gap-2 items-end mb-3 p-3 border rounded bg-gray-50"
                    >
                      <div>
                        <TextInput
                          type="text"
                          value={movimento.formaPagamento.nome}
                          readOnly
                          tabIndex={-1}
                        />
                      </div>
                      <div>
                        <TextInput
                          ref={(el) =>
                            setInputRef(`formaPagamento-${index}`, el)
                          }
                          type="text"
                          value={displayValues.formasPagamento[index] || ""}
                          onChange={(e) =>
                            handleValorChange(
                              "formasPagamento",
                              index,
                              e.target.value
                            )
                          }
                          onKeyDown={(e) =>
                            handleKeyDown(e, `formaPagamento-${index}`)
                          }
                          placeholder="0,00"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Outros Valores */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <FormField
                    label={
                      <span className="flex items-center gap-1.5">
                        Valor Saída
                        <InfoTooltip
                          align="start"
                          text="Informe o valor que saiu do caixa, como pagamento de despesa ou outra retirada. Ele será considerado no cálculo da quebra."
                        />
                      </span>
                    }
                    htmlFor="valor-retirada"
                  >
                    <TextInput
                      id="valor-retirada"
                      ref={(el) => setInputRef("valorRetirada", el)}
                      type="text"
                      value={displayValues.valorRetirada}
                      onChange={(e) =>
                        handleValorChange("valorRetirada", null, e.target.value)
                      }
                      onKeyDown={(e) => handleKeyDown(e, "valorRetirada")}
                      placeholder="0,00"
                    />
                  </FormField>

                  <FormField
                    label={
                      <span className="flex items-center gap-1.5">
                        Sangria de Caixa
                        <InfoTooltip
                          align="start"
                          text="Informe o valor em dinheiro retirado da gaveta para guarda ou depósito. Ele será considerado no cálculo da quebra."
                        />
                      </span>
                    }
                    htmlFor="valor-sangria"
                  >
                    <TextInput
                      id="valor-sangria"
                      ref={(el) => setInputRef("valorSangria", el)}
                      type="text"
                      value={displayValues.valorSangria}
                      onChange={(e) =>
                        handleValorChange("valorSangria", null, e.target.value)
                      }
                      onKeyDown={(e) => handleKeyDown(e, "valorSangria")}
                      placeholder="0,00"
                    />
                  </FormField>

                  <FormField label="Valor Quebra" htmlFor="valor-quebra">
                      <TextInput
                        id="valor-quebra"
                        type="text"
                        value={formatCurrencyMoeda(movimentoData.valorQuebra)}
                        readOnly
                        tabIndex={-1}
                        className={`w-full px-3 py-2 border rounded-md cursor-not-allowed font-semibold ${
                        movimentoData.valorQuebra < 0
                          ? "bg-red-50 text-red-700 border-red-300"
                          : movimentoData.valorQuebra > 0
                          ? "bg-yellow-50 text-yellow-700 border-yellow-300"
                          : "bg-green-50 text-green-700 border-green-300"
                      }`}
                      placeholder="R$ 0,00"
                    />
                  </FormField>
                </div>

                {Math.round((movimentoData.valorQuebra || 0) * 100) !== 0 && (
                  <FormField
                    className="mb-6"
                    label={`Motivo da Quebra de Caixa (${movimentoData.motivoQuebraCaixa.length}/200)`}
                    htmlFor="motivo-quebra-caixa"
                  >
                    <TextAreaInput
                      id="motivo-quebra-caixa"
                      ref={(el) => setInputRef("motivoQuebraCaixa", el)}
                      value={movimentoData.motivoQuebraCaixa}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 200) {
                          setMovimentoData((prev) => ({
                            ...prev,
                            motivoQuebraCaixa: value,
                          }));
                        }
                      }}
                      onKeyDown={(e) => handleKeyDown(e, "motivoQuebraCaixa")}
                      placeholder="Explique o motivo da quebra de caixa"
                      rows="2"
                      maxLength="200"
                    />
                  </FormField>
                )}

                {/* Fundo de Caixa */}
                <div className="mb-6">
                  <h4 className="text-md font-medium mb-4">Fundo de Caixa</h4>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <FormField
                      label={
                        <span className="flex items-center gap-1.5">
                          Fundo Inicial
                          <InfoTooltip
                            align="start"
                            text="É o valor em dinheiro com que o caixa inicia o dia, destinado a servir de troco."
                          />
                        </span>
                      }
                      htmlFor="fundo-esperado"
                    >
                      <TextInput
                        id="fundo-esperado"
                        type="text"
                        value={formatCurrencyMoeda(FUNDO_CAIXA_PADRAO)}
                        readOnly
                        tabIndex={-1}
                        className="w-full px-3 py-2 border rounded-md cursor-not-allowed bg-gray-50"
                      />
                    </FormField>

                    <FormField
                      label={
                        <span className="flex items-center gap-1.5">
                          Caixa Final
                          <InfoTooltip text="Digite aqui o dinheiro total contado do seu caixa, sem descontar o valor de início do caixa." />
                        </span>
                      }
                      htmlFor="total-dinheiro-contado"
                    >
                      <TextInput
                        id="total-dinheiro-contado"
                        ref={(el) => setInputRef("totalDinheiroContado", el)}
                        type="text"
                        value={displayValues.totalDinheiroContado}
                        onChange={(e) =>
                          handleValorChange(
                            "totalDinheiroContado",
                            null,
                            e.target.value
                          )
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(e, "totalDinheiroContado")
                        }
                        placeholder="0,00"
                      />
                    </FormField>

                    <FormField
                      label={
                        <span className="flex items-center gap-1.5">
                          Quebra do Fundo
                          <InfoTooltip
                            align="end"
                            text="Diferença entre o fundo que sobrou no caixa e o fundo inicial. Calculada como Caixa Final menos o dinheiro recebido nas vendas (forma de pagamento Dinheiro) menos o Fundo Inicial. Zerada significa que o fundo está intacto."
                          />
                        </span>
                      }
                      htmlFor="quebra-fundo"
                    >
                      <TextInput
                        id="quebra-fundo"
                        type="text"
                        value={formatCurrencyMoeda(movimentoData.valorQuebraFundo)}
                        readOnly
                        tabIndex={-1}
                        className={`w-full px-3 py-2 border rounded-md cursor-not-allowed font-semibold ${
                          movimentoData.valorQuebraFundo < 0
                            ? "bg-red-50 text-red-700 border-red-300"
                            : movimentoData.valorQuebraFundo > 0
                            ? "bg-yellow-50 text-yellow-700 border-yellow-300"
                            : "bg-green-50 text-green-700 border-green-300"
                        }`}
                        placeholder="R$ 0,00"
                      />
                    </FormField>
                  </div>

                  {Math.round((movimentoData.valorQuebraFundo || 0) * 100) !== 0 && (
                    <FormField
                      label={`Motivo da Quebra do Fundo (${movimentoData.motivoQuebraFundo.length}/200)`}
                      htmlFor="motivo-quebra-fundo"
                    >
                      <TextAreaInput
                        id="motivo-quebra-fundo"
                        ref={(el) => setInputRef("motivoQuebraFundo", el)}
                        value={movimentoData.motivoQuebraFundo}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 200) {
                            setMovimentoData((prev) => ({
                              ...prev,
                              motivoQuebraFundo: value,
                            }));
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, "motivoQuebraFundo")}
                        placeholder="Explique por que o fundo não bateu com os R$ 100,00 esperados"
                        rows="2"
                        maxLength="200"
                      />
                    </FormField>
                  )}
                </div>

                {/* Campo Observação */}
                <FormField
                  className="mb-6"
                  label={
                    <span className="flex items-center gap-1.5">
                      {`Observação (${movimentoData.observacao.length}/200)`}
                      <InfoTooltip
                        align="start"
                        text="Obrigatória sempre que houver um Valor Saída informado, explicando o motivo da saída de dinheiro do caixa."
                      />
                    </span>
                  }
                  htmlFor="movimento-observacao"
                  required={
                    Math.round((movimentoData.valorRetirada || 0) * 100) > 0
                  }
                >
                  <TextAreaInput
                    id="movimento-observacao"
                    ref={(el) => setInputRef("observacao", el)}
                    value={movimentoData.observacao}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 200) {
                        setMovimentoData((prev) => ({
                          ...prev,
                          observacao: value,
                        }));
                      }
                    }}
                    onKeyDown={(e) => handleKeyDown(e, "observacao")}
                    placeholder="Observações do caixa"
                    rows="3"
                    maxLength="200"
                  />
                </FormField>

                {/* Resumo dos Totais */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Resumo dos Totais
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
                    <div>
                      <span className="font-medium">
                        Total Formas Pagamento:
                      </span>
                      <span className="ml-2">
                        {formatCurrencyMoeda(
                          movimentoData.caixaMovimento.reduce(
                            (sum, item) => sum + (item.valorMovimento || 0),
                            0
                          )
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Total Tipos de Caixa:</span>
                      <span className="ml-2">
                        {formatCurrencyMoeda(
                          movimentoData.caixaMovimentoTipoCaixa.reduce(
                            (sum, item) => sum + (item.valorMovimento || 0),
                            0
                          )
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer fixo com botões */}
              <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                <div className="flex gap-2 justify-end">
                  <Button
                    onClick={cancelarMovimento}
                    variant="secondary"
                    icon={X}
                  >
                    Cancelar
                  </Button>

                  <Button
                    onClick={salvarMovimento}
                    disabled={loading}
                    variant="success"
                    icon={Save}
                  >
                    {loading ? "Salvando..." : "Salvar Movimento"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Solicitação de Reabertura */}
        {showReaberturaForm && selectedCaixa && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <MessageSquare size={20} className="text-yellow-600" />
                  Solicitar Reabertura do Caixa
                </h3>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Data do Caixa:</strong>{" "}
                    {formatarData(selectedCaixa.data)}
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    <strong>Valor Total:</strong>{" "}
                    {formatCurrencyMoeda(selectedCaixa.valorTotal)}
                  </p>
                </div>

                <FormField
                  label="Motivo da Solicitação"
                  required
                  htmlFor="motivo-reabertura"
                  hint={`${reaberturaData.motivo.length}/500 caracteres`}
                  className="mb-6"
                >
                  <TextAreaInput
                    id="motivo-reabertura"
                    value={reaberturaData.motivo}
                    onChange={(e) =>
                      setReaberturaData((prev) => ({
                        ...prev,
                        motivo: e.target.value,
                      }))
                    }
                    className="focus:ring-yellow-500"
                    placeholder="Descreva o motivo para solicitar a reabertura do caixa..."
                    rows={4}
                    maxLength={500}
                  />
                </FormField>

                <div className="flex gap-2 justify-end">
                  <Button
                    onClick={cancelarReabertura}
                    variant="secondary"
                    icon={X}
                  >
                    Cancelar
                  </Button>

                  <Button
                    onClick={solicitarReabertura}
                    disabled={loading || !reaberturaData.motivo.trim()}
                    variant="warning"
                    icon={MessageSquare}
                  >
                    {loading ? "Enviando..." : "Enviar Solicitação"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Solicitação de Abertura */}
        {showSolicitacaoAberturaForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle size={20} className="text-orange-600" />
                  Solicitar Abertura de Caixa
                </h3>

                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800 mb-2">
                    <strong>Datas sem caixa identificadas:</strong>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {datasImpedimento.map((data, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded"
                      >
                        {data}
                      </span>
                    ))}
                  </div>
                </div>

                <FormField
                  label="Motivo da Solicitação"
                  required
                  htmlFor="motivo-abertura"
                  hint={`${solicitacaoAberturaCaixa.motivo.length}/500 caracteres`}
                  className="mb-6"
                >
                  <TextAreaInput
                    id="motivo-abertura"
                    value={solicitacaoAberturaCaixa.motivo}
                    onChange={(e) =>
                      setSolicitacaoAberturaCaixa((prev) => ({
                        ...prev,
                        motivo: e.target.value,
                      }))
                    }
                    className="focus:ring-orange-500"
                    placeholder="Explique por que não foram feitos os caixas nas datas mencionadas..."
                    rows={4}
                    maxLength={500}
                  />
                </FormField>

                <div className="flex gap-2 justify-end">
                  <Button
                    onClick={cancelarSolicitacaoAbertura}
                    variant="secondary"
                    icon={X}
                  >
                    Cancelar
                  </Button>

                  <Button
                    onClick={solicitarAberturaCaixa}
                    disabled={
                      loading || !solicitacaoAberturaCaixa.motivo.trim()
                    }
                    variant="warning"
                    icon={MessageSquare}
                  >
                    {loading ? "Enviando..." : "Enviar Solicitação"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </div>
  );
};

export default CaixaDiario;
